import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = BACKEND_URL.replace(/^http/i, "ws"); // https->wss, http->ws

/**
 * TAVEN Live real-time client.
 * Connects to the backend WebSocket room (chat, reactions, viewer presence,
 * pin/product updates, session-state) with heartbeat + auto-reconnect.
 * Fails CLOSED: if the realtime transport is disabled server-side (close 1008)
 * or unreachable, `status` becomes "polling" and the caller keeps its REST polling.
 *
 * @param {string} sessionId
 * @param {string} name display name sent with events
 * @param {(type:string, data:any)=>void} onEvent handler for incoming events
 */
export function useLiveRealtime(sessionId, name, onEvent) {
  const [status, setStatus] = useState("connecting"); // connecting | connected | reconnecting | polling
  const wsRef = useRef(null);
  const handlerRef = useRef(onEvent);
  const attemptRef = useRef(0);
  const heartbeatRef = useRef(null);
  const reconnectRef = useRef(null);
  const closedForGoodRef = useRef(false);
  const mountedRef = useRef(true);

  handlerRef.current = onEvent;

  const connect = useCallback(() => {
    if (!sessionId || !WS_BASE || closedForGoodRef.current) return;
    let ws;
    try {
      ws = new WebSocket(`${WS_BASE}/api/live/ws/${sessionId}?name=${encodeURIComponent(name || "guest")}`);
    } catch {
      setStatus("polling");
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      if (!mountedRef.current) return;
      attemptRef.current = 0;
      setStatus("connected");
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = setInterval(() => {
        try { ws.readyState === 1 && ws.send(JSON.stringify({ type: "ping" })); } catch {}
      }, 20000);
    };

    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data);
        if (m.type === "pong") return;
        handlerRef.current?.(m.type, m.data);
      } catch {}
    };

    ws.onclose = (ev) => {
      clearInterval(heartbeatRef.current);
      if (!mountedRef.current) return;
      // 1008 = policy violation => realtime disabled server-side. Stop and fall back to polling.
      if (ev.code === 1008) {
        closedForGoodRef.current = true;
        setStatus("polling");
        return;
      }
      setStatus("reconnecting");
      const delay = Math.min(1000 * 2 ** attemptRef.current, 15000);
      attemptRef.current += 1;
      // After several failures, surface polling mode (UI still works via REST).
      if (attemptRef.current >= 4) setStatus("polling");
      reconnectRef.current = setTimeout(connect, delay);
    };

    ws.onerror = () => { try { ws.close(); } catch {} };
  }, [sessionId, name]);

  useEffect(() => {
    mountedRef.current = true;
    closedForGoodRef.current = false;
    attemptRef.current = 0;
    connect();
    return () => {
      mountedRef.current = false;
      closedForGoodRef.current = true;
      clearInterval(heartbeatRef.current);
      clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch {}
    };
  }, [connect]);

  const send = useCallback((obj) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === 1) {
      try { ws.send(JSON.stringify(obj)); return true; } catch { return false; }
    }
    return false;
  }, []);

  return { status, send, connected: status === "connected" };
}
